import { TextField, type TextFieldProps } from '@/global/components/TextField'
import { cn } from '@/lib/utils'
import { EyeIcon, EyeOffIcon } from 'lucide-react'
import React, { useMemo, useState } from 'react'
import {
    Controller,
    useFormContext,
    type UseControllerProps,
} from 'react-hook-form'
import { useLocation } from 'react-router-dom'
import IconButton from '../Button/IconButton'
import { useThemeStore } from '../ToggleTheme'
import { Tooltip } from '../ui/tooltip'

/**
 * `TextFieldProps` é a união `input | textarea`, e `Omit` sobre união colapsa
 * nas chaves comuns — `rows` (só textarea) e `type` (só input) sumiriam. O
 * condicional força a distribuição e preserva os dois ramos.
 */
type SemPropsDoControle<T> = T extends unknown
    ? Omit<
          T,
          'name' | 'value' | 'ref' | 'hidden' | 'error' | 'onChange' | 'onBlur'
      >
    : never

/** Ramo `input` da união do `TextField` — ver o spread de `rest` no JSX. */
type PropsDoRamoInput = Extract<TextFieldProps, { multiline?: false }>

type AllowedFormTypes =
    | 'text'
    | 'number'
    | 'password'
    | 'email'
    | 'tel'
    /** Exibe o valor como dinheiro em reais. Não é um `type` do HTML. */
    | 'currency'

export type FormsProps = Omit<UseControllerProps, 'control'> &
    SemPropsDoControle<TextFieldProps> & {
        hidden?: boolean
        name: string
        uppercase?: boolean
        unHelperText?: boolean
        /** Declarado aqui porque só o ramo `input` da união o tem. */
        type?: AllowedFormTypes
        onChange?: React.ChangeEventHandler<HTMLInputElement>
        onBlur?: React.FocusEventHandler<HTMLInputElement>
        /** Se `type=number`, valor mínimo; caso contrário, tamanho mínimo do texto. */
        min?: number
        /** Se `type=number`, valor máximo; caso contrário, tamanho máximo do texto. */
        max?: number
    }

/**
 * Campo de texto ligado ao react-hook-form — o campo mais usado do app. Toda a
 * aparência mora no `TextField`; aqui ficam o `Controller`, a herança de
 * `disabled` do formulário e o erro do Zod na linha auxiliar.
 *
 * Além dos `type` nativos aceita `currency`, que não é `type` de HTML: exibe o
 * valor como dinheiro em reais. Máscara de CPF, CNPJ, telefone e afins entra por
 * `options` (cleave), tratada dentro do `ui/input`.
 *
 * **Não** faz data nem hora: `type` não aceita `date`/`time` de propósito — o
 * controle nativo do navegador foi substituído pelo `FormDatePicker` e pelo
 * `FormFieldTime`.
 */
export const FormField: React.FC<FormsProps> = ({
    name,
    onChange,
    onBlur,
    rules,
    type = 'text',
    hidden,
    options,
    variant = 'filled',
    defaultValue,
    size = 'medium',
    uppercase,
    unHelperText,
    disabled,
    shouldUnregister,
    required,
    min,
    max,
    className,
    endAdornment,
    startAdornment,
    helperText,
    ...rest
}) => {
    const { hash } = useLocation()
    const { control } = useFormContext()
    const theme = useThemeStore(state => state.theme)

    /**
     * `type=password` é o que pede o olho de mostrar/ocultar — o campo nasce
     * oculto e o botão só troca o `type` do input, sem mexer no valor.
     */
    const isSenha = type === 'password'
    const [senhaOculta, setSenhaOculta] = useState(true)

    const styleClass = useMemo(() => {
        if (hidden) return 'hidden!'
        return theme === 'dark' ? 'scheme-dark' : 'scheme-light'
    }, [hidden, theme])

    const hasDisabled = disabled || (control as any)._options?.disabled

    /**
     * O valor é guardado em centavos; aqui ele vira reais. Real é a única
     * moeda do sistema, então não há o que escolher no call-site.
     */
    const isMoeda = type === 'currency'
    const formatarMoeda = (value: string | number) => {
        if (!value) return ''
        const numero =
            typeof value === 'string' ? Number(value.replace(/\D/g, '')) : value
        return (+numero / 100).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        })
    }

    /** Campo desabilitado com URL vira link clicável, como antes. */
    const isValidUrl = (value: string) => {
        const podeSerUrl = (hasDisabled || rest.readOnly) && value
        if (!podeSerUrl) return false
        try {
            new URL(value)
            return true
        } catch (_) {
            return false
        }
    }

    return (
        <Controller
            name={name}
            rules={rules}
            control={control}
            defaultValue={defaultValue || (required ? '' : undefined)}
            shouldUnregister={shouldUnregister}
            render={({
                field: {
                    onChange: onChangeCampo,
                    onBlur: onBlurCampo,
                    value,
                    ...field
                },
                fieldState: { error },
            }) => (
                <Tooltip
                    title={unHelperText && error?.message ? error.message : ''}
                >
                    <TextField
                        {...field}
                        {
                            // O `TextField` é uma união discriminada por
                            // `multiline`, e aqui o discriminante só existe em
                            // runtime (vem do call-site, dentro de `rest`).
                            // Fixar o ramo `input` é o que deixa `type` ser
                            // checado; as props de textarea (`rows`) seguem
                            // sendo repassadas pelo spread.
                            ...(rest as PropsDoRamoInput)
                        }
                        className={cn(
                            // O adorno é `shrink-0` para ícones; com a URL
                            // dentro dele, precisa encolher para o link truncar.
                            isValidUrl(value) &&
                                '[&_[data-slot=input-start-adornment]]:min-w-0 [&_[data-slot=input-start-adornment]]:shrink',
                            styleClass,
                            className,
                        )}
                        data-testid={name}
                        required={required}
                        size={size}
                        variant={variant}
                        options={options}
                        // `currency` não existe no HTML: o campo é de
                        // texto, o que muda é o valor exibido.
                        type={(() => {
                            if (isSenha)
                                return senhaOculta ? 'password' : 'text'
                            return isMoeda ? 'text' : type
                        })()}
                        disabled={hasDisabled}
                        autoFocus={hash === `#${name}`}
                        value={(() => {
                            if (isValidUrl(value)) return ' '
                            if (isMoeda)
                                return formatarMoeda(
                                    value || defaultValue || '',
                                )
                            if (value === null) return ''
                            return value || defaultValue || ''
                        })()}
                        startAdornment={
                            isValidUrl(value) ? (
                                <>
                                    {startAdornment}
                                    <a
                                        href={value}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title={value}
                                        className="max-w-full truncate underline"
                                    >
                                        {value}
                                    </a>
                                </>
                            ) : (
                                startAdornment
                            )
                        }
                        endAdornment={
                            isSenha ? (
                                <IconButton
                                    size="small"
                                    aria-label="Mostrar ou ocultar a senha"
                                    // O `ui/button` marca o ícone com
                                    // `pointer-events-none`: o clique só chega
                                    // aqui, nunca no `<svg>`.
                                    onClick={() =>
                                        setSenhaOculta(prev => !prev)
                                    }
                                >
                                    {senhaOculta ? <EyeOffIcon /> : <EyeIcon />}
                                </IconButton>
                            ) : (
                                endAdornment && (
                                    <div
                                        aria-disabled={hasDisabled}
                                        className="group"
                                    >
                                        {endAdornment}
                                    </div>
                                )
                            )
                        }
                        onChange={(
                            event: React.ChangeEvent<
                                HTMLInputElement | HTMLTextAreaElement
                            >,
                        ) => {
                            if (uppercase) {
                                event.target.value =
                                    event.target.value.toUpperCase()
                            }

                            const { value: novoValor } = event.target

                            if (novoValor && min) {
                                if (type === 'number') {
                                    if (Number(novoValor) < min) return
                                } else if (novoValor.length < min) return
                            }

                            if (novoValor && max) {
                                if (type === 'number') {
                                    if (Number(novoValor) > max) return
                                } else if (novoValor.length > max) return
                            }

                            onChangeCampo(event)
                            onChange?.(
                                event as React.ChangeEvent<HTMLInputElement>,
                            )
                        }}
                        /**
                         * O `onBlur` do call-site sombreava o do react-hook-form
                         * quando os dois vinham no mesmo spread — encadeados na
                         * ordem: primeiro o do formulário, depois o do campo.
                         */
                        onBlur={(
                            event: React.FocusEvent<
                                HTMLInputElement | HTMLTextAreaElement
                            >,
                        ) => {
                            onBlurCampo()
                            onBlur?.(
                                event as React.FocusEvent<HTMLInputElement>,
                            )
                        }}
                        error={(() => {
                            if (hidden || !error) return undefined
                            // Com `unHelperText` não há linha auxiliar: a
                            // mensagem vai para o tooltip e aqui fica só a
                            // marcação de inválido.
                            if (unHelperText) return true
                            return error.message ?? true
                        })()}
                        helperText={helperText}
                        unHelperText={unHelperText}
                    />
                </Tooltip>
            )}
        />
    )
}
